import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";

import {
  FiscalBenefitDeclaration,
  isFiscalBenefitDeclarationComplete,
  type FiscalBenefitDeclarationValue,
} from "@/components/features/fiscal-benefit/FiscalBenefitDeclaration";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  getFiscalBenefitDeclarationForTenant,
  recordFiscalBenefitDeclarationForTenant,
  // Aliased: the component imported above is also called
  // FiscalBenefitDeclaration. This is the API record.
  type FiscalBenefitDeclaration as FiscalBenefitDeclarationRecord,
} from "@/services/fiscalService";

/**
 * A declaration is tied to a specific ejercicio — the "ingresos del ejercicio
 * anterior" motivo is a claim about one year — so it has to be re-signed each
 * calendar year rather than standing indefinitely.
 */
function isCurrentYear(declaredAt: string): boolean {
  const year = new Date(declaredAt).getFullYear();
  return year === new Date().getFullYear();
}

/**
 * Whether a payload from GET .../fiscal-benefit-declaration is a real
 * declaration.
 *
 * Do NOT test `declaration != null`. The endpoint returns `getLatest()`, which
 * is `null` when the client has never declared — but a Nest controller
 * returning null sends an EMPTY BODY, and axios hands that back as `""`, which
 * is not null. EMITTON GROUP SAS has zero rows in fiscal_benefit_declaration
 * and still took the "signed" branch, which is why its card claimed the
 * declaration belonged to a previous ejercicio instead of saying there was
 * none. Test a field that only a real row has.
 */
function isRealDeclaration(
  declaration: unknown,
): declaration is FiscalBenefitDeclarationRecord {
  return (
    typeof declaration === "object" &&
    declaration !== null &&
    typeof (declaration as { declaredAt?: unknown }).declaredAt === "string"
  );
}

/**
 * Whether this client still owes a declaration — none standing, or one that
 * has aged out of the current ejercicio.
 *
 * Exported so the tab badge and this card agree on the answer. They live on
 * different tabs, and an accountant seeing "falta declaración" in the cartera
 * but nothing on the client's page is exactly the confusion one shared rule
 * avoids.
 */
export function isFiscalBenefitDeclarationPending(
  declaration: unknown,
): boolean {
  return (
    !isRealDeclaration(declaration) || !isCurrentYear(declaration.declaredAt)
  );
}

/**
 * Decreto 206/019 declaration for an EXISTING cartera client.
 *
 * Onboarding now requires this before a plan can be activated, but every client
 * onboarded before that — and every client whose declaration has aged out of the
 * current year — has none. Those are exactly the tenants a credit would be
 * granted to with no attestation behind it, and no onboarding gate can reach
 * them because they already finished onboarding.
 *
 * So this surfaces as an ALERT rather than a quiet form: an accountant opening
 * the Perfil fiscal tab should see that something is owed, not have to know to
 * look for it.
 */
export function AccountantFiscalBenefitCard({
  tenantId,
}: {
  tenantId: string;
}): JSX.Element | null {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [value, setValue] = useState<FiscalBenefitDeclarationValue>({
    complies: null,
    motivo: null,
  });

  const { data: declaration, isLoading } = useQuery({
    queryKey: ["accountant", "fiscal-benefit-declaration", tenantId],
    queryFn: () => getFiscalBenefitDeclarationForTenant(tenantId),
    retry: false,
  });

  // Seed the form from the standing declaration so re-signing starts from what
  // was declared last time rather than blank — the usual case is "still true".
  useEffect(() => {
    // Shape check: an empty-body "" is truthy-ish enough to pass a bare
    // `if (declaration)` in some shapes and would seed the form with undefined.
    if (isRealDeclaration(declaration)) {
      setValue({
        complies: declaration.complies,
        motivo: declaration.motivo,
      });
    }
  }, [declaration]);

  const save = useMutation({
    mutationFn: () =>
      recordFiscalBenefitDeclarationForTenant(tenantId, {
        complies: value.complies === true,
        motivo: value.motivo,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["accountant", "fiscal-benefit-declaration", tenantId],
      });
      toast.success(
        t("accountantFiscalBenefit.saved", "Declaración registrada."),
      );
    },
    onError: () => {
      toast.error(
        t(
          "accountantFiscalBenefit.saveError",
          "No pudimos registrar la declaración.",
        ),
      );
    },
  });

  if (isLoading) return null;

  // Shape check, not a null check — see isRealDeclaration above.
  const signed = isRealDeclaration(declaration) ? declaration : null;
  const needsSignature = isFiscalBenefitDeclarationPending(declaration);

  return (
    <Card
      className={
        needsSignature
          ? "border-amber-300 dark:border-amber-800"
          : undefined
      }
    >
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base text-foreground">
          {needsSignature ? (
            <AlertTriangle
              className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400"
              aria-hidden
            />
          ) : (
            <CheckCircle2
              className="h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400"
              aria-hidden
            />
          )}
          {t(
            "accountantFiscalBenefit.title",
            "Beneficio fiscal DGI (Decreto 206/019)",
          )}
        </CardTitle>
        <CardDescription>
          {needsSignature
            ? signed != null
              ? t(
                  "accountantFiscalBenefit.expired",
                  "La declaración de este cliente corresponde a un ejercicio anterior. Confirmá si sigue cumpliendo los requisitos.",
                )
              : t(
                  "accountantFiscalBenefit.missing",
                  "Este cliente todavía no tiene declaración de beneficio fiscal. Es necesaria para aplicar o informar el crédito de 80 UI.",
                )
            : t(
                "accountantFiscalBenefit.signed",
                "Declaración registrada para el ejercicio actual.",
              )}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {needsSignature ? (
          <>
            <FiscalBenefitDeclaration
              audience="accountant"
              idPrefix={`acct-benefit-${tenantId}`}
              value={value}
              onChange={setValue}
              disabled={save.isPending}
            />
            <div className="flex justify-end">
              <Button
                type="button"
                onClick={() => save.mutate()}
                disabled={
                  !isFiscalBenefitDeclarationComplete(value) || save.isPending
                }
              >
                {save.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                ) : null}
                {t("accountantFiscalBenefit.submit", "Enviar declaración")}
              </Button>
            </div>
          </>
        ) : (
          <p className="text-sm text-muted-foreground">
            {signed?.complies
              ? t(
                  "accountantFiscalBenefit.summaryYes",
                  "Declaró cumplir los requisitos.",
                )
              : t(
                  "accountantFiscalBenefit.summaryNo",
                  "Declaró no cumplir los requisitos.",
                )}{" "}
            {signed
              ? new Date(signed.declaredAt).toLocaleDateString("es-UY")
              : null}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
